// Terminal splash: a bash-looking prompt types a command, "runs" it, prints a
// loading line, then dissolves into the site.
//
// Three rules it follows, all deliberate:
//   * once per session (sessionStorage), so a revisit or a back-navigation
//     doesn't replay it — an unskippable intro on every page load is the
//     fastest way to make someone close the tab;
//   * skippable at any moment by click, tap, or any key;
//   * skipped outright under prefers-reduced-motion, where a typing animation
//     is exactly the thing the setting exists to suppress.
//
// The command text comes from the CMS (`splash.command`), so it's editable
// without a rebuild.

const SESSION_KEY = 'portfolio.splashShown';

export interface SplashCopy {
  command: string;
  response: string;
}

export const SPLASH_DEFAULTS: SplashCopy = {
  command: 'whoami --full',
  response: 'loading…',
};

const TYPE_MS = 55;
const PAUSE_BEFORE_ENTER_MS = 320;
const PAUSE_AFTER_RESPONSE_MS = 620;
const FADE_MS = 420;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function alreadyShown(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    // Private browsing can throw. Treat it as "not shown" — worst case the
    // splash plays again, which is harmless.
    return false;
  }
}

function markShown(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    /* nothing to do — it'll just play again next load */
  }
}

/** True when the splash should be skipped entirely for this page load. */
export function shouldSkipSplash(): boolean {
  return alreadyShown() || prefersReducedMotion();
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

function buildOverlay(): {
  overlay: HTMLDivElement;
  typed: HTMLSpanElement;
  output: HTMLDivElement;
} {
  const overlay = document.createElement('div');
  overlay.className = 'rl-splash';
  // Decorative: the real content is announced by the page underneath, and a
  // screen reader shouldn't have to sit through a typing animation.
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = `
    <div class="rl-splash-window">
      <div class="rl-splash-bar">
        <span class="rl-splash-dot"></span>
        <span class="rl-splash-dot"></span>
        <span class="rl-splash-dot"></span>
        <span class="rl-splash-title">jukyokim@portfolio — bash</span>
      </div>
      <div class="rl-splash-body">
        <div class="rl-splash-line">
          <span class="rl-splash-prompt">jukyokim@portfolio</span><span
            class="rl-splash-sep"
          >:</span><span class="rl-splash-path">~</span><span class="rl-splash-sep">$</span>
          <span class="rl-splash-typed"></span><span class="rl-splash-cursor">▋</span>
        </div>
        <div class="rl-splash-output"></div>
      </div>
    </div>
    <div class="rl-splash-skip">press any key to skip</div>
  `;
  return {
    overlay,
    typed: overlay.querySelector<HTMLSpanElement>('.rl-splash-typed')!,
    output: overlay.querySelector<HTMLDivElement>('.rl-splash-output')!,
  };
}

/**
 * Plays the splash over the page. Resolves once it's gone — whether it ran to
 * completion or was skipped. Never rejects: a broken intro must not stop the
 * site from rendering.
 */
export async function playSplash(copy: SplashCopy): Promise<void> {
  if (shouldSkipSplash()) {
    markShown();
    return;
  }
  markShown();

  const { overlay, typed, output } = buildOverlay();
  document.body.appendChild(overlay);
  document.body.classList.add('rl-splash-open');

  const controller = new AbortController();
  const skip = (): void => controller.abort();
  window.addEventListener('keydown', skip, { once: true });
  overlay.addEventListener('click', skip, { once: true });

  try {
    for (const char of [...copy.command]) {
      if (controller.signal.aborted) break;
      typed.textContent = (typed.textContent ?? '') + char;
      await sleep(TYPE_MS, controller.signal);
    }
    if (!controller.signal.aborted) {
      typed.textContent = copy.command;
      await sleep(PAUSE_BEFORE_ENTER_MS, controller.signal);
      output.textContent = copy.response;
      output.classList.add('is-visible');
      await sleep(PAUSE_AFTER_RESPONSE_MS, controller.signal);
    }
  } finally {
    window.removeEventListener('keydown', skip);
    overlay.classList.add('is-leaving');
    document.body.classList.add('rl-entering');
    await new Promise((resolve) => window.setTimeout(resolve, FADE_MS));
    overlay.remove();
    document.body.classList.remove('rl-splash-open');
    window.setTimeout(() => document.body.classList.remove('rl-entering'), FADE_MS);
  }
}
